/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { formatCurrency, formatDateOnly, parseDateRobust } from '../utils/formatters.ts';
import { useCollection } from '../contexts/CollectionContext.tsx';
import PayoutRowExpanded from './PayoutRowExpanded.tsx';
import SalesDashboardSection from './SalesDashboardSection.tsx';
import './AdminPayouts.css';

// Import the new type
import { PayoutOrder } from '../services/payoutService.ts';

interface PayoutRecord {
  id: string;
  userId: string;
  userEmail: string;
  collectionTitle: string;
  collectionHandle?: string;
  orders: PayoutOrder[];
  date: string;
  lastOrderNumberPaid?: number;
  amount: number;
  method: string;
  confirmationNumber: string;
  notes: string;
  createdAt: string;
  createdBy: string;
  status: string;
  updatedBy?: string;
  updatedAt?: string;
}

const AdminPayouts: React.FC = () => {
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [filteredPayouts, setFilteredPayouts] = useState<PayoutRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());
  
  const { 
    userCollections,
    selectedCollectionIndex,
    getCollectionTitle
  } = useCollection();
  
  const [filters, setFilters] = useState({
    searchTerm: '',
    startDate: '',
    endDate: '',
    orderNumber: ''
  });

  const [stats, setStats] = useState({
    totalAmount: 0,
    totalPayouts: 0,
    byCollection: new Map<string, { count: number; total: number }>(),
    byUser: new Map<string, { count: number; total: number; email: string }>()
  });

  const togglePayoutExpanded = (payoutId: string) => {
    setExpandedPayouts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(payoutId)) {
        newSet.delete(payoutId);
      } else {
        newSet.add(payoutId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const loadPayouts = async () => {
      setLoading(true);
      setError(null);

      try {
        const payoutsRef = collection(db, 'payouts');
        const q = query(payoutsRef, orderBy('createdAt', 'desc'));
        
        const snapshot = await getDocs(q);
        const payoutsData: PayoutRecord[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          
          let orders: PayoutOrder[] = [];
          if (data.orders) {
            orders = data.orders;
          } else if (data.orderIds && data.orderNumbers) {
            orders = data.orderIds.map((id: string, index: number) => ({
              id,
              number: data.orderNumbers[index] || ''
            }));
          }
          
          payoutsData.push({
            id: doc.id,
            ...data,
            orders,
            collectionHandle: data.collectionHandle || undefined,
            updatedBy: data.updatedBy || undefined,
            updatedAt: data.updatedAt || undefined
          } as PayoutRecord);
        });

        setPayouts(payoutsData);
      } catch (err) {
        console.error('Error loading payouts:', err);
        setError('Failed to load payouts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPayouts();
  }, []);

  const selectedHandle = userCollections[selectedCollectionIndex];

  // The highest order number covered by any of the selected collection's
  // payouts, or null if it's never had one - decides between "Amount
  // earned" (first payout still pending) and "Amount earned since your
  // last payment", and narrows how far back that figure's order-history
  // fetch needs to look. Order-number based rather than date based - see
  // useEarnedSincePayout for why (payout.date is free-text and historically
  // non-ISO, which is exactly what caused the Chrome-vs-Safari mismatch).
  const lastOrderNumberPaidForSelectedCollection = useMemo(() => {
    if (!selectedHandle) return null;

    const matching = payouts.filter((payout) => {
      if (payout.collectionHandle) {
        return payout.collectionHandle === selectedHandle;
      }
      const expectedTitle = getCollectionTitle(selectedHandle);
      return payout.collectionTitle === expectedTitle;
    });

    if (matching.length === 0) return null;

    const numbers = matching
      .map((payout) => payout.lastOrderNumberPaid)
      .filter((n): n is number => typeof n === 'number' && !isNaN(n));

    // None of the matching payouts have been backfilled with
    // lastOrderNumberPaid yet - fall back to no cutoff (full history)
    // rather than treating "no data" as "paid through order 0".
    if (numbers.length === 0) return null;

    return Math.max(...numbers);
  }, [payouts, selectedHandle, getCollectionTitle]);

  const calculateStats = (payoutsData: PayoutRecord[]) => {
    const totalAmount = payoutsData.reduce((sum, p) => sum + p.amount, 0);
    const totalPayouts = payoutsData.length;

    const byCollection = new Map<string, { count: number; total: number }>();
    payoutsData.forEach(payout => {
      const existing = byCollection.get(payout.collectionTitle) || { count: 0, total: 0 };
      byCollection.set(payout.collectionTitle, {
        count: existing.count + 1,
        total: existing.total + payout.amount
      });
    });

    const byUser = new Map<string, { count: number; total: number; email: string }>();
    payoutsData.forEach(payout => {
      const existing = byUser.get(payout.userId) || { count: 0, total: 0, email: payout.userEmail };
      byUser.set(payout.userId, {
        count: existing.count + 1,
        total: existing.total + payout.amount,
        email: payout.userEmail
      });
    });

    setStats({ totalAmount, totalPayouts, byCollection, byUser });
  };

  useEffect(() => {
    let filtered = [...payouts];

    const selectedHandle = userCollections[selectedCollectionIndex];
    if (selectedHandle) {
      filtered = filtered.filter(payout => {
        if (payout.collectionHandle) {
          return payout.collectionHandle === selectedHandle;
        }
        const expectedTitle = getCollectionTitle(selectedHandle);
        return payout.collectionTitle === expectedTitle;
      });
    }

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(payout =>
        payout.userEmail.toLowerCase().includes(searchLower) ||
        payout.collectionTitle.toLowerCase().includes(searchLower) ||
        payout.confirmationNumber.toLowerCase().includes(searchLower) ||
        payout.notes.toLowerCase().includes(searchLower)
      );
    }

    if (filters.startDate) {
      filtered = filtered.filter(payout =>
        parseDateRobust(payout.date) >= new Date(filters.startDate)
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(payout =>
        parseDateRobust(payout.date) <= new Date(filters.endDate)
      );
    }

    if (filters.orderNumber) {
      const orderNumberLower = filters.orderNumber.toLowerCase();
      filtered = filtered.filter(payout => {
        if (!payout.orders || !Array.isArray(payout.orders)) {
          return false;
        }
        return payout.orders.some(order => {
          if (!order || !order.number) return false;
          const orderNum = String(order.number).toLowerCase();
          return orderNum.includes(orderNumberLower);
        });
      });
    }

    setFilteredPayouts(filtered);
    calculateStats(filtered);
  }, [filters, payouts, selectedCollectionIndex, userCollections, getCollectionTitle]);

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      startDate: '',
      endDate: '',
      orderNumber: ''
    });
  };

  const exportToCSV = () => {
    const headers = ['Date', 'User Email', 'Collection', 'Amount', 'Method', 'Confirmation', 'Order Numbers', 'Notes', 'Created By'];
    const rows = filteredPayouts.map(payout => [
      payout.date,
      payout.userEmail,
      payout.collectionTitle,
      payout.amount.toFixed(2),
      payout.method,
      payout.confirmationNumber,
      payout.orders.map(order => order.number).join('; '),
      payout.notes,
      payout.createdBy
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const selectedCollectionName = userCollections[selectedCollectionIndex] 
    ? getCollectionTitle(userCollections[selectedCollectionIndex])
    : null;

  if (loading) {
    return (
      <div className="admin-payouts-container">
        <div className="loading-state">
          <h2>Loading payouts...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-payouts-container">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-payouts-container">
      <SalesDashboardSection
        selectedCollectionName={selectedCollectionName}
        selectedHandle={selectedHandle}
        lastOrderNumberPaid={lastOrderNumberPaidForSelectedCollection}
        payouts={payouts}
        payoutsLoading={loading}
      />

      <div className="admin-header">
        <h1>All Payouts (Admin)</h1>
        <button onClick={exportToCSV} className="btn-export">
          📥 Export to CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="stats-container">
        <div className="stat-card total">
          <div className="stat-label">
            {selectedCollectionName
              ? `Total for ${selectedCollectionName}`
              : 'Total Paid Out'}
          </div>
          <div className="stat-value">{formatCurrency(stats.totalAmount)}</div>
          <div className="stat-count">{stats.totalPayouts} payments</div>
        </div>
      </div>

      {/* Additional Filters */}
      <div className="filters-container">
        <h3>Additional Filters</h3>
        
        <div className="filters-grid">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Email, collection, confirmation..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Order Number</label>
            <input
              type="text"
              placeholder="Search by order number..."
              value={filters.orderNumber}
              onChange={(e) => {
                try {
                  setFilters({ ...filters, orderNumber: e.target.value });
                } catch (error) {
                  console.error('Error filtering by order number:', error);
                }
              }}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group filter-actions">
            <button onClick={clearFilters} className="btn-clear-filters">
              Clear Filters
            </button>
          </div>
        </div>

        <div className="filter-results">
          Showing {filteredPayouts.length} of {payouts.length} payouts
        </div>
      </div>

      {/* Breakdown Tables */}
      <div className="breakdown-grid">
        {/* By Collection */}
        <div className="breakdown-card">
          <h3>By Collection</h3>
          <div className="breakdown-table">
            <table className="desktop-table">
              <thead>
                <tr>
                  <th>Collection</th>
                  <th>Payments</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(stats.byCollection.entries())
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([collection, data]) => (
                    <tr key={collection}>
                      <td>{collection}</td>
                      <td>{data.count}</td>
                      <td className="amount">{formatCurrency(data.total)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="mobile-cards">
              {Array.from(stats.byCollection.entries())
                .sort((a, b) => b[1].total - a[1].total)
                .map(([collection, data]) => (
                  <div key={collection} className="breakdown-mobile-card">
                    <div className="mobile-card-title">{collection}</div>
                    <div className="mobile-card-stats">
                      <div className="mobile-card-stat">
                        <span className="mobile-stat-label">Payments:</span>
                        <span className="mobile-stat-value">{data.count}</span>
                      </div>
                      <div className="mobile-card-stat">
                        <span className="mobile-stat-label">Total:</span>
                        <span className="mobile-stat-value amount">{formatCurrency(data.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* By User */}
        <div className="breakdown-card">
          <h3>By User</h3>
          <div className="breakdown-table">
            <table className="desktop-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Payments</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(stats.byUser.entries())
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([userId, data]) => (
                    <tr key={userId}>
                      <td>{data.email}</td>
                      <td>{data.count}</td>
                      <td className="amount">{formatCurrency(data.total)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="mobile-cards">
              {Array.from(stats.byUser.entries())
                .sort((a, b) => b[1].total - a[1].total)
                .map(([userId, data]) => (
                  <div key={userId} className="breakdown-mobile-card">
                    <div className="mobile-card-title">{data.email}</div>
                    <div className="mobile-card-stats">
                      <div className="mobile-card-stat">
                        <span className="mobile-stat-label">Payments:</span>
                        <span className="mobile-stat-value">{data.count}</span>
                      </div>
                      <div className="mobile-card-stat">
                        <span className="mobile-stat-label">Total:</span>
                        <span className="mobile-stat-value amount">{formatCurrency(data.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Payouts List - RESPONSIVE WITH DESKTOP TABLE AND MOBILE CARDS */}
      <div className="payouts-list-container">
        <h3>Payment Details</h3>
        
        {filteredPayouts.length === 0 ? (
          <div className="empty-state">
            <p>No payouts match your filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="payouts-table desktop-only">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Date</th>
                    <th>Collection</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Confirmation</th>
                    <th>Order(s)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayouts.map((payout) => (
                    <React.Fragment key={payout.id}>
                      <tr 
                        onClick={() => togglePayoutExpanded(payout.id)}
                        style={{ cursor: 'pointer' }}
                        className={expandedPayouts.has(payout.id) ? 'expanded-row' : ''}
                      >
                        <td className="expand-cell">
                          <span className="expand-icon">
                            {expandedPayouts.has(payout.id) ? '▼' : '▶'}
                          </span>
                        </td>
                        <td className="date-cell">{formatDateOnly(payout.date)}</td>
                   
                        <td className="collection-cell">{payout.collectionTitle}</td>
                        <td className="amount-cell">{formatCurrency(payout.amount)}</td>
                        <td className="method-cell">{payout.method}</td>
                        <td className="confirmation-cell">{payout.confirmationNumber}</td>
                        <td className="orders-cell">
                         <div className="orders-list">
                            {payout.orders.length > 1 && <span className="badge">{payout.orders.length} total</span>}
                            {payout.orders.length === 1 && payout.orders.map(order => order.number).join(', ')}
                        </div>
                        </td>
                      </tr>

                      {expandedPayouts.has(payout.id) && (
                        <tr className="expanded-details-row">
                          <td colSpan={8}>
                            <PayoutRowExpanded payout={payout} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="payouts-mobile-cards mobile-only">
              {filteredPayouts.map((payout) => (
                <div key={payout.id} className="payout-mobile-card">
                  <div 
                    className="payout-card-header"
                    onClick={() => togglePayoutExpanded(payout.id)}
                  >
                    <div className="payout-card-main">
                         <div className={`payout-card-main ${expandedPayouts.has(payout.id) ? 'is-expanded' : ''}`}></div>
                      <div className="payout-card-row">
                        <span className="payout-card-label">Date: </span>
                        <span className="payout-card-value">{formatDateOnly(payout.date)}</span>
                      </div>
                      <div className="payout-card-row">
                        <span className="payout-card-label">Collection: </span>
                        <span className="payout-card-value">{payout.collectionTitle}</span>
                      </div>
                         <div className="payout-card-row">
                        <span className="payout-card-label">Amount: </span>
                        <span className="payout-card-value amount">{formatCurrency(payout.amount)}</span>
                      </div>
                      <div className="payout-card-row">
                        <span className="payout-card-label">Method: </span>
                        <span className="payout-card-value">{payout.method}</span>
                      </div>
                      <div className="payout-card-row">
                        <span className="payout-card-label">Confirmation: </span>
                        <span className="payout-card-value">{payout.confirmationNumber}</span>
                      </div>
                      <div className="payout-card-row">
                        <span className="payout-card-label">Orders: </span>
                        <span className="payout-card-value">
                          {payout.orders.map(order => order.number).join(', ')}
                        </span>
                      </div>
                    </div>
                    <div className="payout-card-expand">
                      <span className="expand-icon">
                        {expandedPayouts.has(payout.id) ? 'hide details ▲' : 'show details ▼'}
                      </span>
                    </div>
                  </div>
                  
                  {expandedPayouts.has(payout.id) && (
                    <div className="payout-card-expanded">
                      <PayoutRowExpanded payout={payout} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPayouts;